import type { Ref } from 'vue';

import { cn } from '~/utils';
import {
	createAnimationManager,
	createCancellableDelay
} from '~~/utils/animation';
import { computed, onUnmounted, readonly, ref, watch } from 'vue';

// Types
export type AnimationStage =
	| 'header-visible'
	| 'contextual-appearing'
	| 'header-hiding'
	| 'contextual-only'
	| 'header-returning'
	| 'contextual-hiding';

export interface AnimationStep {
	stage: AnimationStage;
	delay?: number;
}

export interface StateTransition {
	id: string;
	targetStage: AnimationStage;
	steps: readonly AnimationStep[];
}

// Constants
export const ANIMATION_DELAYS = {
	APPEAR: 500,
	HIDE: 300,
	TRANSITION: 500
} as const;

export const HEADER_HEIGHTS = {
	BOTH_VISIBLE: 'h-[111px] md:h-[139px]',
	PORTAL_ONLY: 'h-[60px] md:h-20',
	CONTEXTUAL_ONLY: 'h-[51px] md:h-[59px]',
	NONE: 'h-0'
} as const;

export const BASE_CLASSES = {
	WRAPPER:
		'fixed top-0 z-[49] w-full transform transition-transform duration-300',
	PORTAL_HEADER:
		'relative flex h-[60px] w-full flex-col transform transition-transform duration-300 md:h-20',
	CONTEXTUAL_HEADER:
		'relative flex w-full flex-col transform transition-transform duration-300'
} as const;

export function shouldContextualBeVisible(
	animationStage: AnimationStage,
	isContextualHeaderVisible: boolean
): boolean {
	switch (animationStage) {
		case 'header-visible':
			return isContextualHeaderVisible;
		case 'contextual-appearing':
		case 'contextual-only':
		case 'header-hiding':
			return true;
		case 'header-returning':
		case 'contextual-hiding':
			return false;
		default:
			return isContextualHeaderVisible;
	}
}

export function shouldHeaderBeVisible(
	animationStage: AnimationStage,
	isHeaderVisible: boolean,
	isContextualHeaderVisible: boolean
): boolean {
	// Keep portal header visible internally when contextual header is present
	if (isContextualHeaderVisible) {
		return true;
	}

	switch (animationStage) {
		case 'header-visible':
			return isHeaderVisible;
		case 'contextual-appearing':
		case 'header-returning':
		case 'contextual-hiding':
			return true;
		case 'header-hiding':
		case 'contextual-only':
			return false;
		default:
			return isHeaderVisible;
	}
}

export function getWrapperHeight(
	isPortalVisible: boolean,
	isContextualVisible: boolean
): string {
	if (isPortalVisible && isContextualVisible) {
		return HEADER_HEIGHTS.BOTH_VISIBLE;
	} else if (isPortalVisible && !isContextualVisible) {
		return HEADER_HEIGHTS.PORTAL_ONLY;
	} else if (!isPortalVisible && isContextualVisible) {
		return HEADER_HEIGHTS.CONTEXTUAL_ONLY;
	}

	return HEADER_HEIGHTS.NONE;
}

export function getWrapperClasses(
	animationStage: AnimationStage,
	wrapperHeight: string
): string {
	switch (animationStage) {
		case 'header-visible':
		case 'contextual-appearing':
		case 'header-returning':
		case 'contextual-hiding':
			return `${BASE_CLASSES.WRAPPER} ${wrapperHeight}`;
		case 'header-hiding':
		case 'contextual-only':
			return `${BASE_CLASSES.WRAPPER} ${wrapperHeight} -translate-y-[60px] md:-translate-y-[80px]`;
		default:
			return `${BASE_CLASSES.WRAPPER} ${wrapperHeight}`;
	}
}

export function getPortalHeaderClasses(
	shouldShow: boolean,
	contextualIsVisible: boolean
): string {
	return cn(BASE_CLASSES.PORTAL_HEADER, 'z-50 lg:z-[60]', {
		'translate-y-0': !contextualIsVisible && shouldShow,
		'-translate-y-full': !contextualIsVisible && !shouldShow
	});
}

export function getContextualHeaderClasses(
	shouldShow: boolean,
	isMenuBeingShown: boolean
): string {
	const zIndex = isMenuBeingShown ? 'z-40 lg:z-[45]' : 'z-[45]';

	return cn(BASE_CLASSES.CONTEXTUAL_HEADER, zIndex, {
		'translate-y-0': shouldShow,
		'translate-y-[-140px]': !shouldShow
	});
}

export function getCorrectAnimationStage(
	isContextualHeaderVisible: boolean,
	isHeaderVisible: boolean
): AnimationStage {
	if (isContextualHeaderVisible && isHeaderVisible) {
		return 'contextual-appearing';
	} else if (!isContextualHeaderVisible && isHeaderVisible) {
		return 'header-visible';
	} else if (isContextualHeaderVisible && !isHeaderVisible) {
		return 'contextual-only';
	} else {
		return 'header-visible';
	}
}

// State Queue Manager for sequential animations
export class StateQueue {
	private queue: StateTransition[] = [];
	private isProcessing = false;
	private animationManager = createAnimationManager();

	enqueue(transition: StateTransition): void {
		this.queue = this.queue.filter(
			t => t.targetStage !== transition.targetStage
		);
		this.queue.push(transition);

		if (!this.isProcessing) {
			this.process();
		}
	}

	private async process(): Promise<void> {
		if (this.isProcessing || this.queue.length === 0) {
			return;
		}

		this.isProcessing = true;

		while (this.queue.length > 0) {
			const transition = this.queue.shift();
			if (!transition) break;

			try {
				await this.executeTransition(transition);
			} catch (error) {
				if (error instanceof Error && error.message !== 'Animation cancelled') {
					console.error('Animation transition failed:', error);
				}
			}
		}

		this.isProcessing = false;
	}

	private async executeTransition(transition: StateTransition): Promise<void> {
		const controller = this.animationManager.startNew();

		for (const step of transition.steps) {
			this.onStageChange?.(step.stage);

			if (step.delay) {
				await createCancellableDelay(step.delay, controller);
			}
		}
	}

	clear(): void {
		this.queue = [];
		this.animationManager.cancelCurrent();
		this.isProcessing = false;
	}

	onStageChange?: (stage: AnimationStage) => void;

	get isEmpty(): boolean {
		return this.queue.length === 0 && !this.isProcessing;
	}
}

// Predefined transitions
export const TRANSITIONS = {
	HEADER_TO_CONTEXTUAL: {
		id: 'header-to-contextual',
		targetStage: 'contextual-only' as AnimationStage,
		steps: [
			{
				stage: 'contextual-appearing' as AnimationStage,
				delay: ANIMATION_DELAYS.APPEAR
			},
			{
				stage: 'header-hiding' as AnimationStage,
				delay: ANIMATION_DELAYS.APPEAR
			},
			{ stage: 'contextual-only' as AnimationStage }
		]
	},
	CONTEXTUAL_TO_HEADER: {
		id: 'contextual-to-header',
		targetStage: 'header-visible' as AnimationStage,
		steps: [
			{
				stage: 'header-returning' as AnimationStage,
				delay: ANIMATION_DELAYS.APPEAR
			},
			{
				stage: 'contextual-hiding' as AnimationStage,
				delay: ANIMATION_DELAYS.HIDE
			},
			{ stage: 'header-visible' as AnimationStage }
		]
	},
	DIRECT_TO_CONTEXTUAL: {
		id: 'direct-to-contextual',
		targetStage: 'contextual-appearing' as AnimationStage,
		steps: [{ stage: 'contextual-appearing' as AnimationStage }]
	},
	DIRECT_TO_HEADER: {
		id: 'direct-to-header',
		targetStage: 'header-visible' as AnimationStage,
		steps: [{ stage: 'header-visible' as AnimationStage }]
	}
} as const;

export function useContextualHeaderAnimation(
	isContextualHeaderVisible: Ref<boolean>,
	isHeaderVisible: Ref<boolean>,
	headerVariant: Ref<'translucide' | 'primary'>
) {
	const animationStage = ref<AnimationStage>('header-visible');
	const isTransitioning = ref<boolean>(false);
	const localHeaderVariant = ref<'translucide' | 'primary'>(
		headerVariant.value
	);

	const stateQueue = new StateQueue();

	stateQueue.onStageChange = (stage: AnimationStage) => {
		animationStage.value = stage;
		isTransitioning.value = !stateQueue.isEmpty;
	};

	function getTransition(
		currentStage: AnimationStage,
		targetStage: AnimationStage
	): StateTransition {
		if (
			currentStage === 'header-visible' &&
			targetStage === 'contextual-only'
		) {
			return TRANSITIONS.HEADER_TO_CONTEXTUAL;
		}

		if (
			currentStage === 'contextual-only' &&
			targetStage === 'header-visible'
		) {
			return TRANSITIONS.CONTEXTUAL_TO_HEADER;
		}

		if (targetStage === 'contextual-appearing') {
			return TRANSITIONS.DIRECT_TO_CONTEXTUAL;
		}

		return TRANSITIONS.DIRECT_TO_HEADER;
	}

	function resetToDefault() {
		stateQueue.clear();
		animationStage.value = 'header-visible';
		isTransitioning.value = false;
		localHeaderVariant.value = 'translucide';
	}

	watch(
		[isContextualHeaderVisible, isHeaderVisible],
		([contextualVisible, headerVisible]) => {
			// If both are in their default state, reset animation states immediately
			if (!contextualVisible && headerVisible) {
				resetToDefault();
				return;
			}

			const currentStage = animationStage.value;
			const targetStage = getCorrectAnimationStage(
				isContextualHeaderVisible.value,
				isHeaderVisible.value
			);

			if (currentStage !== targetStage) {
				const transition = getTransition(currentStage, targetStage);
				stateQueue.enqueue(transition);
			}
		}
	);

	watch(
		[headerVariant, isTransitioning, isContextualHeaderVisible],
		([newVariant, transitioning, contextualVisible]) => {
			// Only delay primary -> translucide transition if:
			// 1. We're transitioning AND
			// 2. The contextual header is still visible (meaning we're in a contextual transition)
			// 3. We're not at the top of the page (which should reset immediately)
			if (
				localHeaderVariant.value === 'primary' &&
				newVariant === 'translucide' &&
				transitioning &&
				contextualVisible
			) {
				// Don't update yet, wait for transitions to finish
				return;
			}

			localHeaderVariant.value = newVariant;
		},
		{ immediate: true }
	);

	const shouldContextualBeVisibleComputed = computed<boolean>(() => {
		return shouldContextualBeVisible(
			animationStage.value,
			isContextualHeaderVisible.value
		);
	});

	const shouldHeaderBeVisibleComputed = computed<boolean>(() => {
		return shouldHeaderBeVisible(
			animationStage.value,
			isHeaderVisible.value,
			isContextualHeaderVisible.value
		);
	});

	const wrapperHeight = computed<string>(() => {
		return getWrapperHeight(
			shouldHeaderBeVisibleComputed.value,
			shouldContextualBeVisibleComputed.value
		);
	});

	const wrapperClasses = computed<string>(() => {
		return getWrapperClasses(animationStage.value, wrapperHeight.value);
	});

	const portalHeaderClasses = computed<string>(() => {
		return getPortalHeaderClasses(
			shouldHeaderBeVisibleComputed.value,
			isContextualHeaderVisible.value
		);
	});

	const contextualHeaderClasses = computed<string>(() => {
		return getContextualHeaderClasses(
			shouldContextualBeVisibleComputed.value,
			false
		);
	});

	function cleanup() {
		stateQueue.clear();
	}

	onUnmounted(cleanup);

	return {
		animationStage: readonly(animationStage),
		isTransitioning: readonly(isTransitioning),
		localHeaderVariant: readonly(localHeaderVariant),
		shouldContextualBeVisible: shouldContextualBeVisibleComputed,
		shouldHeaderBeVisible: shouldHeaderBeVisibleComputed,
		wrapperHeight,
		wrapperClasses,
		portalHeaderClasses,
		contextualHeaderClasses,
		resetToDefault,
		cleanup
	};
}
